import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import {
  CreditCard,
  Video,
  Users,
  Mail,
  FileText,
  BarChart,
  Package,
  Calendar,
} from 'lucide-react';
import GeometricBackground from '@/components/GeometricBackground';

const FeaturesPage: React.FC = () => {
  const features = [
    {
      icon: CreditCard,
      title: 'Payments Integration',
      description: 'Seamless payment processing for lessons, courses, and subscriptions',
      benefits: ['Secure payment gateway', 'Recurring billing', 'Invoice management', 'Multiple currencies'],
    },
    {
      icon: Video,
      title: 'Meetings Integration',
      description: 'Built-in video conferencing for online lessons and masterclasses',
      benefits: ['One-click lesson start', 'Recording capabilities', 'Screen sharing', 'Breakout rooms'],
    },
    {
      icon: Users,
      title: 'User Management',
      description: 'Complete student and teacher management system',
      benefits: ['Student profiles', 'Progress tracking', 'Role-based access', 'Parent accounts'],
    },
    {
      icon: Mail,
      title: 'Newsletter System',
      description: 'Engage your community with professional email campaigns',
      benefits: ['Email templates', 'Subscriber management', 'Analytics tracking', 'Automated campaigns'],
    },
    {
      icon: FileText,
      title: 'Blog Platform',
      description: 'Share your expertise and attract new students',
      benefits: ['SEO optimized', 'Rich text editor', 'Media library', 'Categories & tags'],
    },
    {
      icon: BarChart,
      title: 'Analytics Dashboard',
      description: 'Track your business performance with detailed insights',
      benefits: ['Revenue reports', 'Student engagement', 'Lesson statistics', 'Growth metrics'],
    },
    {
      icon: Package,
      title: 'Custom Products & Categories',
      description: 'Sell courses, sheet music, and digital products',
      benefits: ['Product catalog', 'Digital downloads', 'Inventory tracking', 'Discount codes'],
    },
    {
      icon: Calendar,
      title: 'Custom Calendar with Bookings',
      description: 'Intelligent scheduling system for lessons and events',
      benefits: ['Availability management', 'Automated reminders', 'Recurring bookings', 'Time zone support'],
    },
  ] as const;

  return (
    <>
      <Helmet>
        <title>Features - Arrangelly</title>
        <meta
          name="description"
          content="Discover all the powerful features of Arrangelly: Payments, Meetings, User Management, Newsletter, Blogs, Analytics, Products, and Calendar with Bookings."
        />
      </Helmet>

      <div className="relative min-h-screen overflow-hidden">
        <GeometricBackground />
        <div className="relative z-10">
          <section className="relative py-20 overflow-hidden">
            <div className="container mx-auto px-4 relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center mb-16"
              >
                <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-violet-600 to-secondary bg-clip-text text-transparent">
                  Powerful Features
                </h1>
                <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                  Everything you need to run a professional music education business, all in one
                  integrated platform
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    whileHover={{ scale: 1.02, y: -5 }}
                    className="glass-panel rounded-[1.75rem] p-8 shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-violet-500 flex items-center justify-center flex-shrink-0">
                        <feature.icon className="w-7 h-7 text-primary-foreground" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                        <p className="text-slate-600">{feature.description}</p>
                      </div>
                    </div>

                    <ul className="space-y-2 mt-6">
                      {feature.benefits.map((benefit) => (
                        <li key={benefit} className="flex items-center gap-2 text-slate-600">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default FeaturesPage;


